import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import client from '@/api/client';
import type {} from '@/api/types';
import { Users, Clock, Building2, UserCheck, AlertTriangle, Layers, BedDouble, IndianRupee, Gauge, TrendingUp, UserPlus, CalendarDays, CalendarRange } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, LineChart, Line, CartesianGrid, AreaChart, Area } from 'recharts';

function fmtShort(n: number) {
  if (n >= 100000) return '₹' + (n / 100000).toFixed(1) + 'L';
  if (n >= 1000) return '₹' + (n / 1000).toFixed(1) + 'k';
  return '₹' + n.toLocaleString('en-IN');
}

function StatCard({
  label, value, icon, color, onClick, badge, sub,
}: {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  onClick?: () => void;
  badge?: string;
  sub?: string;
}) {
  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl p-5 shadow-sm border border-gray-100 ${onClick ? 'cursor-pointer hover:shadow-md transition' : ''}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-gray-500 font-medium">{label}</span>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>{icon}</div>
      </div>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
      {badge && <p className="text-xs text-amber-600 mt-1 font-medium">{badge}</p>}
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

export function AdminDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery<any>({
    queryKey: ['admin-dashboard'],
    queryFn: () => client.get('/api/admin/dashboard'),
  });

  if (isLoading) return <div className="p-8 text-gray-400">{t('common.loading')}</div>;

  const s = data ?? {
    totalOwners: 0, activeOwners: 0, pendingOwners: 0,
    totalSupervisors: 0, pendingHostels: 0, activeHostels: 0,
    totalFloors: 0, totalRooms: 0, totalBeds: 0, occupiedBeds: 0, vacantBeds: 0,
    reservedBeds: 0, blockedBeds: 0, occupancyRate: 0,
    collectedThisMonth: 0, totalDueThisMonth: 0, collectionRate: 0,
    monthlyRevenue: [], occupancyTrend: [],
    totalUsersOnboarded: 0, usersOnboardedThisWeek: 0, usersOnboardedThisMonth: 0, userOnboardingTrend: [],
  };

  const occupancyData = [
    { name: t('dashboard.occupiedBeds'), value: s.occupiedBeds, color: '#0d9488' },
    { name: t('common.reserved'), value: s.reservedBeds, color: '#f59e0b' },
    { name: t('dashboard.blockedBeds'), value: s.blockedBeds, color: '#6b7280' },
    { name: t('dashboard.vacantBeds'), value: s.vacantBeds, color: '#22c55e' },
  ];

  const infraData = [
    { name: t('admin.floors', 'Floors'), value: s.totalFloors, fill: '#6366f1' },
    { name: t('admin.roomsShort', 'Rooms'), value: s.totalRooms, fill: '#06b6d4' },
    { name: t('admin.bedsShort', 'Beds'), value: s.totalBeds, fill: '#6b7280' },
  ];

  const peopleData = [
    { name: t('admin.activeOwners', 'Active Owners'), value: s.activeOwners, fill: '#22c55e' },
    { name: t('admin.pendingOwners', 'Pending Owners'), value: s.pendingOwners, fill: '#f59e0b' },
    { name: t('nav.supervisors'), value: s.totalSupervisors, fill: '#8b5cf6' },
  ];

  const hostelData = [
    { name: t('common.active'), value: s.activeHostels, fill: '#14b8a6' },
    { name: t('rent.pending'), value: s.pendingHostels, fill: '#f97316' },
  ];

  const revenueData = (s.monthlyRevenue ?? []).map((d: any) => ({
    month: d.month,
    amount: Number(d.amount),
  }));

  const occupancyTrendData = (s.occupancyTrend ?? []).map((d: any) => ({
    month: d.month,
    occupancy: d.occupancy,
  }));

  const onboardingTrendData = (s.userOnboardingTrend ?? []).map((d: any) => ({
    month: d.month,
    count: d.count,
  }));

  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('admin.dashboardTitle', 'Admin Dashboard')}</h1>

      {/* Alert banner */}
      {(s.pendingOwners > 0 || s.pendingHostels > 0) && (
        <div className="mb-5 flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          <AlertTriangle size={16} className="shrink-0" />
          <span>
            {[
              s.pendingOwners > 0 && t('admin.ownersPendingApproval', '{{count}} owner(s) pending approval', { count: s.pendingOwners }),
              s.pendingHostels > 0 && t('admin.hostelsPendingApproval', '{{count}} hostel(s) pending approval', { count: s.pendingHostels }),
            ].filter(Boolean).join(' · ')}
          </span>
          <button onClick={() => void navigate({ to: '/admin/owners' })} className="ml-auto text-amber-700 font-medium hover:underline whitespace-nowrap">{t('admin.reviewArrow', 'Review →')}</button>
        </div>
      )}

      {/* KPI Cards - Business Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label={t('dashboard.totalProperties')} value={s.activeHostels + s.pendingHostels} icon={<Building2 size={18} className="text-teal-600" />} color="bg-teal-50" />
        <StatCard label={t('dashboard.totalCapacity')} value={`${s.totalBeds} ${t('common.beds')}`} icon={<BedDouble size={18} className="text-blue-600" />} color="bg-blue-50" />
        <StatCard label={t('admin.occupancy', 'Occupancy')} value={`${s.occupancyRate}%`} icon={<Gauge size={18} className="text-green-600" />} color="bg-green-50" />
        <StatCard label={t('dashboard.thisMonthRevenue')} value={fmtShort(s.collectedThisMonth)} icon={<IndianRupee size={18} className="text-purple-600" />} color="bg-purple-50" />
      </div>

      {/* People & hostels */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
        <StatCard label={t('admin.totalOwners', 'Total Owners')} value={s.totalOwners} icon={<Users size={18} className="text-blue-600" />} color="bg-blue-50" onClick={() => void navigate({ to: '/admin/owners' })} />
        <StatCard label={t('admin.pendingOwnerApprovals', 'Pending Owner Approvals')} value={s.pendingOwners} icon={<Clock size={18} className="text-amber-600" />} color="bg-amber-50" onClick={() => void navigate({ to: '/admin/owners' })} badge={s.pendingOwners > 0 ? t('admin.actionRequired', 'Action required') : undefined} />
        <StatCard label={t('admin.activeOwners', 'Active Owners')} value={s.activeOwners} icon={<UserCheck size={18} className="text-green-600" />} color="bg-green-50" />
        <StatCard label={t('nav.supervisors')} value={s.totalSupervisors} icon={<Users size={18} className="text-violet-600" />} color="bg-violet-50" />
        <StatCard label={t('admin.pendingHostels', 'Pending Hostels')} value={s.pendingHostels} icon={<Clock size={18} className="text-orange-600" />} color="bg-orange-50" onClick={() => void navigate({ to: '/admin/hostels' })} badge={s.pendingHostels > 0 ? t('admin.awaitingReview', 'Awaiting review') : undefined} />
        <StatCard label={t('dashboard.activeHostels')} value={s.activeHostels} icon={<Building2 size={18} className="text-teal-600" />} color="bg-teal-50" onClick={() => void navigate({ to: '/admin/hostels' })} />
      </div>

      {/* User onboarding */}
      <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('admin.userOnboarding', 'User Onboarding')}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard label={t('admin.totalUsersOnboarded', 'Total Users Onboarded')} value={s.totalUsersOnboarded} icon={<UserPlus size={18} className="text-blue-600" />} color="bg-blue-50" onClick={() => void navigate({ to: '/admin/users' })} />
        <StatCard label={t('admin.onboardedThisWeek', 'Onboarded This Week')} value={s.usersOnboardedThisWeek} icon={<CalendarDays size={18} className="text-teal-600" />} color="bg-teal-50" />
        <StatCard label={t('admin.onboardedThisMonth', 'Onboarded This Month')} value={s.usersOnboardedThisMonth} icon={<CalendarRange size={18} className="text-violet-600" />} color="bg-violet-50" />
      </div>

      {onboardingTrendData.length > 0 && (
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 mb-6">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('admin.onboardingTrend', 'User Onboarding Trend (Last 12 Months)')}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={onboardingTrendData}>
              <defs><linearGradient id="onboardGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#7c3aed" stopOpacity={0.3} /><stop offset="95%" stopColor="#7c3aed" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(value: number) => [value, t('admin.usersOnboarded', 'Users Onboarded')]} />
              <Area type="monotone" dataKey="count" stroke="#7c3aed" strokeWidth={2} fill="url(#onboardGrad)" dot={{ r: 3, fill: '#7c3aed' }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Bed Occupancy donut */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('dashboard.bedOccupancy')}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={occupancyData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={4} dataKey="value">
                {occupancyData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
              </Pie>
              <Tooltip />
              <Legend verticalAlign="bottom" height={30} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Infrastructure bar chart */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('admin.infrastructureOverview', 'Infrastructure Overview')}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={infraData}>
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {infraData.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* People distribution */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('admin.peopleDistribution', 'People Distribution')}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={peopleData}>
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {peopleData.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Hostel status */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('admin.hostelStatus', 'Hostel Status')}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={hostelData}>
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {hostelData.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Trend */}
        {revenueData.length > 0 && (
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 lg:col-span-2">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('dashboard.revenueTrend')}</h3>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={revenueData}>
                <defs><linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0d9488" stopOpacity={0.3} /><stop offset="95%" stopColor="#0d9488" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(value: number) => [fmtShort(value), t('dashboard.revenue')]} />
                <Area type="monotone" dataKey="amount" stroke="#0d9488" strokeWidth={2} fill="url(#revGrad)" dot={{ r: 3, fill: '#0d9488' }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Occupancy Trend */}
        {occupancyTrendData.length > 0 && (
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 lg:col-span-2">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('dashboard.occupancyTrend')}</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={occupancyTrendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(value: number) => [`${value.toFixed(1)}%`, t('admin.occupancy', 'Occupancy')]} />
                <Line type="monotone" dataKey="occupancy" stroke="#0d9488" strokeWidth={2} dot={{ r: 3, fill: '#0d9488' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Financial Snapshot */}
      <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 mb-6">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
          <TrendingUp size={14} className="text-teal-500" /> {t('dashboard.financialSnapshot')}
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-teal-50 rounded-lg p-3 text-center">
            <p className="text-xs text-teal-600">{t('dashboard.collectedThisMonth')}</p>
            <p className="text-lg font-bold text-teal-700">{fmtShort(s.collectedThisMonth)}</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-3 text-center">
            <p className="text-xs text-amber-600">{t('rent.totalDue')}</p>
            <p className="text-lg font-bold text-amber-700">{fmtShort(s.totalDueThisMonth)}</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <p className="text-xs text-blue-600">{t('dashboard.collectionRate')}</p>
            <p className="text-lg font-bold text-blue-700">{s.collectionRate.toFixed(1)}%</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <p className="text-xs text-green-600">{t('dashboard.occupancyRate')}</p>
            <p className="text-lg font-bold text-green-700">{s.occupancyRate}%</p>
          </div>
        </div>
      </div>

      {/* Infrastructure stat cards */}
      <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('admin.infrastructure', 'Infrastructure')}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        <StatCard label={t('admin.totalFloors', 'Total Floors')} value={s.totalFloors} icon={<Layers size={18} className="text-indigo-600" />} color="bg-indigo-50" />
        <StatCard label={t('dashboard.totalRooms')} value={s.totalRooms} icon={<Building2 size={18} className="text-cyan-600" />} color="bg-cyan-50" />
        <StatCard label={t('dashboard.totalBeds')} value={s.totalBeds} icon={<BedDouble size={18} className="text-gray-600" />} color="bg-gray-50" />
        <StatCard label={t('admin.occupied', 'Occupied')} value={s.occupiedBeds} icon={<BedDouble size={18} className="text-teal-500" />} color="bg-teal-50" sub={`${s.occupancyRate}%`} />
        <StatCard label={t('admin.vacant', 'Vacant')} value={s.vacantBeds} icon={<BedDouble size={18} className="text-green-600" />} color="bg-green-50" />
      </div>
    </div>
  );
}
